import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
// Material imports
import DialogContentText from "@mui/material/DialogContentText";
import makeStyles from "@mui/styles/makeStyles";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
// Limitless imports
import Grid from "@limitless/modern/layout/Grid";
import Paper from "@limitless/modern/surfaces/paper/Paper";
import Typography from "@limitless/modern/data-display/typography/Typography";
import Dialog from "@limitless/modern/feedback/dialog/Dialog";
import PendingFilters from "./PendingFilters";
import IconButton from "@limitless/modern/inputs/icon-button/IconButton";
import AvailableFilters from "./AvailableFilters";
import PaymentTabContent from "./PaymentTabContent";
import Download from "@limitless/icons/Download";
import {
    useAuthenticationContext,
    useExpertPendingQuestionsContext,
    useExpertAvailableQuestionsContext
} from "@limitless/expert";
import { useAppConfig } from "@limitless/common/hooks";

const useStyles = makeStyles({
    tab: {
        minWidth: 82,
        textTransform: "none",
        alignItems: "flex-start",
        paddingLeft: 2,
        marginRight: 10
    }
});

const downloadFile = ({ data, fileName, fileType }) => {
    const blob = new Blob([data], { type: fileType });

    const a = document.createElement("a");
    a.download = fileName;
    a.href = window.URL.createObjectURL(blob);
    const clickEvt = new MouseEvent("click", {
        view: window,
        bubbles: true,
        cancelable: true
    });
    a.dispatchEvent(clickEvt);
    a.remove();
};

const Rewards = ({ theme }) => {
    const [pendingInfoOpen, setPendingInfoOpen] = React.useState(false);
    const [tabId, selectedTabId] = React.useState(0);
    const classes = useStyles();
    const history = useHistory();
    const { isAuthenticated } = useAuthenticationContext();
    const { loadExpertPendingQuestions, pendingQuestions } = useExpertPendingQuestionsContext();
    const { loadExpertAvailableRewards, availableQuestions } = useExpertAvailableQuestionsContext();
    const {
        config: { loading: configLoading }
    } = useAppConfig();

    React.useEffect(() => {
        if (history?.location?.state) {
            selectedTabId(1);
            loadExpertPendingQuestions();
        }
    }, [history?.location?.state]);

    React.useEffect(() => {
        if (isAuthenticated && !configLoading) {
            if (TABS[tabId].id === "pendingTab") {
                loadExpertPendingQuestions();
            } else {
                loadExpertAvailableRewards();
            }
        }
    }, [isAuthenticated, configLoading, tabId, TABS]);

    const handlePendingInfoToggle = () => {
        setPendingInfoOpen(!pendingInfoOpen);
    };

    const handleChangeTab = (event, newValue) => {
        selectedTabId(newValue);
    };

    const handleDownload = () => {
        const headers = ["Group name,Fee type,Date awarded,Fee,Fee Share"];
        const usersCsv = availableQuestions.filtered.reduce((fees, user) => {
            const { formatReportDate, groupName, formattedProportion, type, fee } = user;
            if (type !== "Responded") {
                fees.push([groupName, type, formatReportDate, fee].join(","));
            } else {
                fees.push([groupName, type, formatReportDate, fee, formattedProportion].join(","));
            }

            return fees;
        }, []);

        downloadFile({
            data: [...headers, ...usersCsv].join("\n"),
            fileName: "availableReward.csv",
            fileType: "text/csv"
        });
    };

    const TABS = React.useMemo(
        () => [
            {
                label: "Available",
                id: "availableTab",
                component: (
                    <PaymentTabContent
                        rewardQuestion={availableQuestions}
                        theme={theme}
                        header={"All rewards that make up your available balance (up to a max of 500 records)"}
                        tab={tabId}
                    />
                )
            },
            {
                label: "Pending",
                id: "pendingTab",
                component: (
                    <PaymentTabContent
                        rewardQuestion={pendingQuestions}
                        isPendingReward
                        theme={theme}
                        handleToggle={handlePendingInfoToggle}
                        header={`All tasks to which you’ve contributed and for which you may receive a fee.${" "}`}
                        tab={tabId}
                    />
                )
            }
        ],
        [pendingQuestions, availableQuestions]
    );

    const renderPendingRewardInfo = React.useMemo(() => {
        return (
            <Dialog
                open={pendingInfoOpen}
                onClose={handlePendingInfoToggle}
                aria-labelledby="pending-rewards-info"
                title="Pending Rewards"
                onOkClick={() => handlePendingInfoToggle(false)}
                okText="Close"
                PaperProps={{ elevation: 1, style: { padding: 24, minWidth: 567 } }}
            >
                <DialogContentText variant="subtitle2">What are Pending Rewards?</DialogContentText>
                <DialogContentText variant="body2">
                    Pending Rewards are all tasks to which you&apos;ve contributed and may receive a fee for. This
                    includes both respond and review tasks. Pending Reward tasks may currently require a response, be in
                    review or be waiting on the customer. Fees are rewarded when the task closes. Expand each Pending
                    Reward task to view the &apos;Fee allocation date&apos;.
                </DialogContentText>
                <DialogContentText variant="subtitle2">When would I not receive a fee ?</DialogContentText>
                <DialogContentText variant="body2">
                    If a task has multiple responders, fees are allocated on the outcome of the peer review process. If
                    your contribution to the task was not endorsed by your peers, you will not receive a (portion of
                    the) fee. If you are the only responder to a task, you will receive the full fee amount. Expand each
                    Pending Reward task to view the &apos;Number of contributors&apos;.
                </DialogContentText>
                <DialogContentText variant="subtitle2">
                    Why can&apos;t I see all the tasks I&apos;ve contributed to?
                </DialogContentText>
                <DialogContentText variant="body2">
                    Some of the tasks that you&apos;ve contributed to may not be eligible for a fee. For example, if a
                    task that you&apos;ve contributed to is forwarded to the client&apos;s in-house customer support
                    team, you may not receive a fee. Therefore, this task would not be displayed as &apos;Pending&apos;
                    fee allocation.
                </DialogContentText>
            </Dialog>
        );
    }, [pendingInfoOpen]);

    const tab = React.useMemo(() => TABS[tabId].component, [tabId, TABS, pendingQuestions]);

    return (
        <>
            <Paper
                sx={{
                    maxWidth: "unset",
                    minHeight: "100%",
                    height: "calc(100vh - 133px)"
                }}
                elevation={2}
            >
                <Grid container sx={{ padding: theme => theme.spacing(3), rowGap: theme => theme.spacing(1) }}>
                    <Grid item xs={6}>
                        <Grid
                            item
                            container
                            sx={{ rowGap: theme => theme.spacing(1) }}
                            xs={12}
                            component={Typography}
                            variant="h6"
                            color="text.primary"
                            id="pendingMsgSummaryHeading"
                        >
                            Your Rewards
                        </Grid>
                    </Grid>
                    <Grid item xs={6} display="flex" justifyContent="flex-end" container>
                        {TABS[tabId].id === "availableTab" && (
                            <IconButton
                                icon={<Download sx={{ color: theme.palette.primary.dark }} />}
                                label="Download"
                                id="DownloadIconButton"
                                onClick={handleDownload}
                                style={{ marginRight: "14px" }}
                            />
                        )}
                        {TABS[tabId].id === "pendingTab" ? <PendingFilters /> : <AvailableFilters />}
                    </Grid>

                    <Grid item xs={12}>
                        <Tabs
                            value={tabId}
                            onChange={handleChangeTab}
                            TabIndicatorProps={{
                                sx: { backgroundColor: theme => theme.palette.primary, height: "3px" }
                            }}
                        >   
                            {TABS.map((tab, index) => {
                                return (
                                    <Tab
                                        key={index}
                                        id={tab.id}
                                        classes={{ root: classes.tab }}
                                        label={
                                            <Typography variant="subtitle1" color={index===tabId?"textPrimary":"textSecondary"}>
                                                {tab.label}
                                            </Typography>
                                        }
                                    />
                                );
                            })}
                        </Tabs>
                    </Grid>

                    <Grid
                        item
                        xs={12}
                        sx={{
                            height: "calc(100vh - 264px)",
                            overflow: "auto",
                            marginTop: 1
                        }}
                    >
                        {tab}
                    </Grid>
                </Grid>
            </Paper>
            {renderPendingRewardInfo}
        </>
    );
};

Rewards.propTypes = {
    navigate: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
    t: PropTypes.func.isRequired
};

export default Rewards;
